import React, { useMemo, useContext } from 'react'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router'

// evertel
import { useUI, Icon, Row } from '@evertel/web/ui'
import { MessageWallContext } from '@evertel/message'
import { EmojiList } from './EmojiList'
import { DisplayRoomMessage, DisplayThreadMessage } from '@evertel/stores'
import { APIDataBlueUser } from '@evertel/types'

type OptionProps = {
    option: string
    messageId?: number
    params?: any
}

interface HoverToolbarProps {
    message: DisplayRoomMessage | DisplayThreadMessage
    visible: boolean
    canRetract: boolean
    user: APIDataBlueUser
    canReact?: boolean
    canForward?: boolean
    canReply?: boolean
    onClickOption: (option: OptionProps) => void
    onOpenEmojiPicker: (target: string) => void
    ownerId?: number
    variant?: 'list-item' | 'search' | 'reply'
}

// Type predicate function
function isDisplayRoomMessage(message: DisplayRoomMessage | DisplayThreadMessage): message is DisplayRoomMessage {
    return 'roomId' in message
}

const HoverToolbar: React.FC<HoverToolbarProps> = observer(({
    message,
    visible,
    canRetract = false,
    canReact = true,
    canForward = false,
    canReply = true,
    onOpenEmojiPicker,
    onClickOption,
    ownerId,
    variant = 'list-item',
    user
}) => {

    const { currentUserEmojisController, setIsReadByModalOpen, setSelectedMessageId } = useContext(MessageWallContext)

    const { isConfirmed } = useUI()

    const onClickReads = () => {
        setSelectedMessageId(message.id)
        setIsReadByModalOpen(true)
    }

    const onClickForward = () => {
        onClickOption({
            option: 'forward-message',
            messageId: message.id
        })
    }

    const onClickRetract = async () => {
        const confirmed = await isConfirmed({
            title: 'Retract message?',
            message: 'Are you sure you want to retract this message. This will remove it from the conversation?',
            acceptButton: {
                label: 'Retract',
                color: 'danger'
            }
        })
        if (confirmed) {
            onClickOption({
                option: 'retract',
                messageId: message.id
            })
        }
    }
    const emojis = useMemo(() => currentUserEmojisController.getFrequent(5, true), [currentUserEmojisController.frequent])

    // This is for screen readers to read the message
    const messageAriaLabel = `message from ${user.firstName} ${user.lastName} at ${new Date(message.publishedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', 'th,')}`

    const canReallyReply = canReply // no bots
                        && variant !== 'reply' //not on reply header (web only)
                        && isDisplayRoomMessage(message) //only rooms
                        && message.repliesToId == null //not already replied
                        && (message.type !== 'event' || message.meta?.eventType?.startsWith('document')) //only document events

    // important to minimize what is rendered with each message
    if (!visible) return null

    return (
        <Row className="hover-toolbar">
            {/* REACTIONS */}
            {(canReact) && (
                <div
                    className="reactions"
                    id={`msg-reactions-${message.id}`}
                >
                    <EmojiList
                        emojis={emojis}
                        onClickOption={onClickOption}
                        messageId={message.id}
                    />
                    <button
                        className="option emojipicker-btn"
                        onClick={() => onOpenEmojiPicker(`#msg-reactions-${message.id}`)}
                        aria-label={`React with emoji to ${messageAriaLabel}`}
                    >
                        <Icon
                            name="grin"
                        />
                    </button>
                </div>
            )}

            {/* READS */}
            <button
                className="option"
                onClick={onClickReads}
                data-tooltip="Viewed By"
                data-tooltip-pos="top-left-adjust"
                aria-label={`See who has read ${messageAriaLabel}`}
            >
                <Icon
                    name="eye"
                />
            </button>

            {/* REPLIES */}
            {canReallyReply && (
                <Link className="option replies-btn" to={`/room/${message.roomId}/replies/${message.id}`}
                    data-tooltip="Reply"
                    data-tooltip-pos="top-left-adjust"
                    aria-label={`Reply to ${messageAriaLabel}`}
                >
                    <Icon
                        name="reply"
                    />
                </Link>
            )}

            {/* FORWARD */}
            {(canForward) && (
                <button
                    className="option"
                    onClick={onClickForward}
                    data-tooltip="Forward"
                    data-tooltip-pos="top-left-adjust"
                    aria-label={`Forward ${messageAriaLabel}`}
                >
                    <Icon
                        name="share"
                    />
                </button>
            )}

            {/* RETRACT */}
            {(canRetract) && (
                <button
                    className="option"
                    onClick={onClickRetract}
                    data-tooltip="Retract"
                    data-tooltip-pos="top-left-adjust"
                    aria-label={`Retract ${messageAriaLabel}`}
                >
                    <Icon
                        color="danger"
                        name="trash-alt"
                    />
                </button>
            )}
        </Row>
    )
})

export { HoverToolbar }
